import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api, { API_BASE } from "@/lib/api";
import { Spinner } from "@/components/Loaders";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { House, Storefront, DownloadSimple, Megaphone, QrCode } from "@phosphor-icons/react";

const FlyerPreview = ({ kind, s }) => {
  const site = (s.site_url || "").replace(/\/$/, "");
  const contact = [s.support_phone && `Call +${s.support_phone}`, s.support_whatsapp && `WhatsApp +${s.support_whatsapp}`, s.support_email].filter(Boolean).join("  ·  ");
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-8 shadow-sm">
      <p className="font-heading text-2xl font-extrabold text-primary">{s.business_name || "Avinya Bookings"}</p>
      <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-amber-600">Book · Relax · Celebrate</p>
      {kind === "owner" ? (
        <>
          <h3 className="mt-6 font-heading text-2xl font-bold text-stone-900">List Your Property on Avinya</h3>
          <p className="mt-2 text-sm text-stone-500">Reach customers looking for farmhouses, resorts, banquet halls and function halls across Hyderabad and nearby areas.</p>
          <ul className="mt-4 space-y-1.5 text-sm text-stone-700">
            {["Free listing — you set your prices & availability", "You accept every booking request", "Secure online advance; balance at your venue", "Owner dashboard for calendar, bookings & earnings", "Dedicated onboarding & support"].map((b) => (
              <li key={b} className="flex gap-2"><span className="text-primary">✓</span>{b}</li>
            ))}
          </ul>
          <div className="mt-6 rounded-xl bg-primary py-3 text-center font-bold text-white">LIST YOUR PROPERTY {site && `→ ${site}/register`}</div>
        </>
      ) : (
        <>
          <h3 className="mt-6 font-heading text-2xl font-bold text-stone-900">Find your perfect place to book with Avinya</h3>
          <p className="mt-2 text-sm text-stone-500">Discover and book farmhouses, resorts, banquet halls and function halls for your next getaway or celebration.</p>
          <div className="mt-4 flex flex-wrap gap-2">{["Farmhouses", "Resorts", "Banquet Halls", "Function Halls"].map((c) => <span key={c} className="rounded-full bg-amber-50 px-3 py-1 text-sm font-medium text-amber-700">{c}</span>)}</div>
          <p className="mt-4 text-sm text-stone-600">Verified listings · Clear pricing · Secure payment · Booking support</p>
          <div className="mt-6 rounded-xl bg-primary py-3 text-center font-bold text-white">BOOK NOW {site && `→ ${site}`}</div>
        </>
      )}
      <p className="mt-6 rounded-xl bg-stone-100 py-2.5 text-center text-xs font-medium text-stone-700">{contact || s.business_name}</p>
      {site && <p className="mt-2 flex items-center justify-center gap-1 text-center text-[11px] text-stone-400"><QrCode size={13} /> A scannable QR code to {kind === "owner" ? "the listing page" : "the site"} is included in the downloaded PDF.</p>}
    </div>
  );
};

export default function AdminOwnerKit() {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => setErr(true)); }, []);

  const download = (kind) => {
    const token = localStorage.getItem("avb_token");
    fetch(`${API_BASE}/admin/owner-kit/pdf?kind=${kind}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((blob) => { const u = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = u; a.download = `avinya_${kind}_flyer.pdf`; a.click(); URL.revokeObjectURL(u); })
      .catch(() => toast.error("Could not generate the flyer. Please try again."));
  };

  if (err) return <DashboardLayout nav={adminNav} title="Admin"><div className="mt-10 rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700">Couldn't load marketing content. Please refresh.</div></DashboardLayout>;
  if (!s) return <DashboardLayout nav={adminNav} title="Admin"><div className="flex justify-center py-24"><Spinner /></div></DashboardLayout>;

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <div className="flex items-center gap-2"><Megaphone size={26} className="text-primary" /><h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Marketing Kit</h1></div>
      <p className="mt-1 text-sm text-stone-500">Print-ready flyers built from your real contact details in Settings. Update Settings to change the phone, WhatsApp, email or website shown here.</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div data-testid="owner-flyer">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><Storefront size={20} className="text-primary" /> Owner Acquisition Flyer</h2>
            <Button data-testid="download-owner-flyer" onClick={() => download("owner")} className="gap-2 rounded-xl bg-primary hover:bg-green-900"><DownloadSimple size={16} /> Download PDF</Button>
          </div>
          <FlyerPreview kind="owner" s={s} />
        </div>
        <div data-testid="customer-flyer">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><House size={20} className="text-primary" /> Customer Launch Flyer</h2>
            <Button data-testid="download-customer-flyer" onClick={() => download("customer")} className="gap-2 rounded-xl bg-primary hover:bg-green-900"><DownloadSimple size={16} /> Download PDF</Button>
          </div>
          <FlyerPreview kind="customer" s={s} />
        </div>
      </div>
      <p className="mt-6 text-xs text-stone-400">These flyers make no guaranteed-booking or "largest/best" claims. Share on WhatsApp, Instagram or print for local outreach.</p>
    </DashboardLayout>
  );
}
